import React, { useCallback } from 'react';
import { Button } from '@shadcn/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shadcn/components/ui/select';
import { RefreshCw } from 'lucide-react';
import { TRIGGER_TYPES, TRIGGER_TYPE_LABELS } from '../../constants';
import useAutomationRules from '../../hooks/useAutomationRules';
import RuleCard from './RuleCard';
import RuleSearchInput from '../Shared/RuleSearchInput';
import 'react-toastify/dist/ReactToastify.css';

const RuleList = ({ onRuleChange, defaultTriggerType, ruleOptions }) => {
  const {
    rules,
    isLoading,
    filters,
    totalPages,
    currentPage,
    updateFilters,
    goToPage,
    toggleRule,
    deleteRule,
    executeRule,
    duplicateRule,
    refresh,
  } = useAutomationRules({ defaultTriggerType });

  const handleToggle = useCallback(async (id) => {
    await toggleRule(id);
    onRuleChange?.();
  }, [toggleRule, onRuleChange]);

  const handleDelete = useCallback(async (id) => {
    await deleteRule(id);
    onRuleChange?.();
  }, [deleteRule, onRuleChange]);

  const handleExecute = useCallback(async (id) => {
    await executeRule(id);
    onRuleChange?.();
  }, [executeRule, onRuleChange]);

  const handleDuplicate = useCallback(async (id) => {
    await duplicateRule(id);
    onRuleChange?.();
  }, [duplicateRule, onRuleChange]);

  const handleRuleSelect = useCallback((ruleId) => {
    if (!ruleId) {
      updateFilters({ search: '' });
      return;
    }
    const rule = (ruleOptions || []).find(o => o.value === ruleId);
    updateFilters({ search: rule ? rule.label : '' });
  }, [ruleOptions, updateFilters]);

  const handleSearch = useCallback((text) => {
    updateFilters({ search: text });
  }, [updateFilters]);

  const selectedRuleId = filters.search
    ? (ruleOptions || []).find(o => o.label === filters.search)?.value || ''
    : '';

  return (
    <div>
      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <RuleSearchInput
          value={selectedRuleId}
          onChange={handleRuleSelect}
          onSearch={handleSearch}
          options={ruleOptions || []}
        />
        {!defaultTriggerType && (
          <Select
            value={filters.triggerType || 'all'}
            onValueChange={(val) => updateFilters({ triggerType: val === 'all' ? '' : val })}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Trigger type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All triggers</SelectItem>
              {Object.entries(TRIGGER_TYPES).map(([key, value]) => (
                <SelectItem key={value} value={value}>
                  {TRIGGER_TYPE_LABELS[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select
          value={filters.enabled !== '' ? String(filters.enabled) : 'all'}
          onValueChange={(val) => updateFilters({ enabled: val === 'all' ? '' : val })}
        >
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="true">Enabled</SelectItem>
            <SelectItem value="false">Disabled</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="ghost" size="sm" onClick={refresh}>
          <RefreshCw size={14} />
        </Button>
      </div>

      {/* Rules List */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-20 bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : rules.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <p className="text-lg">No rules found</p>
          <p className="text-sm mt-1">Create your first automation rule to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rules.map(rule => (
            <RuleCard
              key={rule._id}
              rule={rule}
              onToggle={handleToggle}
              onDelete={handleDelete}
              onExecute={handleExecute}
              onDuplicate={handleDuplicate}
              showScheduleInfo={defaultTriggerType === 'schedule'}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => goToPage(currentPage - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-gray-500">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage >= totalPages}
            onClick={() => goToPage(currentPage + 1)}
          >
            Next
          </Button>
        </div>
      )}

    </div>
  );
};

export default RuleList;
